import {
    getReplenishmentConfig,
    updateReplenishmentConfig,
    type RatePolicyBasis,
    type ReplenishmentConfig,
    type ReplenishmentTierScope,
} from '@api/replenishment-planning';
import MacWindowModal, { macWindowClasses } from '@components/ui/MacWindowModal';
import useToaster from '@hooks/toaster/useToaster';
import {
    Anchor,
    Button,
    Divider,
    Group,
    Loader,
    NumberInput,
    SegmentedControl,
    Select,
    Stack,
    Text,
} from '@mantine/core';
import { Fragment, useEffect, useState, type FC } from 'react';

import classes from './ReorderRulesModal.module.css';

/**
 * Which half of the replenishment config is being edited.
 *
 * They are split because they answer to different people in different places. TARGET_DOH is
 * the plan's input — how much cover to hold — so it lives with the plan, mirroring how
 * distribution keeps its DoH settings on its own plan view. REORDER is the level at which
 * inventory health flags a SKU, so it lives there.
 *
 * `tierScope` rides with TARGET_DOH because both numbers resolve through it, and it is the
 * plan that owns the sizing question.
 */
export type ReplenishmentSettingsMode = 'TARGET_DOH' | 'REORDER';

type Props = {
    opened: boolean;
    mode: ReplenishmentSettingsMode;
    onClose: () => void;
    /**
     * Opens the per-SKU bulk upload. Passed in rather than owned here because the upload modal
     * belongs to the grid (it refetches on success); this screen only offers the door, since
     * a per-SKU window is the same policy at a finer grain and belongs with the rest of it.
     */
    onOpenUpload?: () => void;
};

/**
 * Which precomputed ranking replenishment reads. Every scope is calculated from the brand's
 * ONE tier definition, so this picks a denominator — it does not define anything.
 */
const SCOPE_OPTIONS: { value: ReplenishmentTierScope; label: string }[] = [
    { value: 'BRAND', label: 'Across the whole catalogue' },
    { value: 'PRODUCT_GROUP', label: 'Within each product group' },
    { value: 'CATEGORY', label: 'Within each category' },
];

const SCOPE_HINT: Record<ReplenishmentTierScope, string> = {
    BRAND: 'Tier A is the SKUs making the top share of total sales.',
    PRODUCT_GROUP:
        'Tier A is the SKUs making the top share of THEIR product group — so a small group still gets its own A, B and C.',
    CATEGORY: 'Tier A is the SKUs making the top share of their own category.',
};

/** A tier row being edited. Empty strings are "not set", which is not the same as 0. */
type TierDraft = {
    tier: string;
    targetDohDays: number | '';
    leadTimeDays: number | '';
    safetyDays: number | '';
    /** The DRR lookback this tier's reorder points are SIZED on. Blank ⇒ the default below. */
    drrWindowDays: number | '';
};

/**
 * Per-tier target days-of-cover and reorder rules.
 *
 * Two DIFFERENT numbers live here, deliberately side by side and never mixed:
 *
 *   • Target DoH — how much cover to HOLD. The plan's input; it works out WHEN to order
 *     from lead times on its own, so the brand never states a date.
 *   • Reorder point — the level at which a human should act NOW, on the inventory-health
 *     screen. Derived, not typed: DRR × (lead time + target DoH + safety).
 *
 * The brand edits the terms, not the answer, because the answer moves with the run rate and
 * a typed threshold would go stale the week demand changed.
 */
const ReplenishmentSettingsModal: FC<Props> = ({
    opened,
    mode,
    onClose,
    onOpenUpload,
}) => {
    const isTargetDoh = mode === 'TARGET_DOH';
    const { showToastSuccess, showToastWarning } = useToaster();
    const [config, setConfig] = useState<ReplenishmentConfig | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [scope, setScope] = useState<ReplenishmentTierScope>('BRAND');
    const [defaultDoh, setDefaultDoh] = useState<number | ''>(30);
    const [defaultSafety, setDefaultSafety] = useState<number | ''>(0);
    /** Blank means "inherit the org's sales lookback", which is shown as the placeholder. */
    const [defaultRopWindow, setDefaultRopWindow] = useState<number | ''>('');
    const [inheritedRopWindow, setInheritedRopWindow] = useState<number | null>(null);
    /** Which direction reorder points read demand in. Merchant-wide — never per tier or SKU. */
    const [ropBasis, setRopBasis] = useState<RatePolicyBasis>('BACKWARD');
    const [ropBasisInherited, setRopBasisInherited] = useState(true);
    const [drafts, setDrafts] = useState<TierDraft[]>([]);

    useEffect(() => {
        if (!opened) return;
        let cancelled = false;
        setLoading(true);
        getReplenishmentConfig()
            .then((res) => {
                if (cancelled) return;
                setConfig(res);
                setScope(res.tierScope);
                setDefaultDoh(res.defaultTargetDohDays);
                setDefaultSafety(res.defaultReorderSafetyDays);
                setDrafts(
                    res.tierLabels.map((tier) => ({
                        tier,
                        // Absent means "inherit the default" — rendered blank rather than
                        // pre-filled with the default, so saving does not silently pin every
                        // tier to a number nobody chose.
                        targetDohDays: res.tierTargetDohDays?.[tier] ?? '',
                        leadTimeDays: res.reorderRules?.[tier]?.leadTimeDays ?? '',
                        safetyDays: res.reorderRules?.[tier]?.safetyDays ?? '',
                        drrWindowDays: res.reorderRules?.[tier]?.drrWindowDays ?? '',
                    })),
                );
                // Blank when the number is the org lookback showing through rather than a
                // choice made here — the placeholder then states what is actually inherited,
                // so saving cannot silently pin the merchant to a value nobody picked.
                setDefaultRopWindow(
                    res.isRopDrrWindowConfigured ? (res.defaultRopDrrWindowDays ?? '') : '',
                );
                setInheritedRopWindow(res.defaultRopDrrWindowDays ?? null);
                // Always a concrete value — the server resolves the org default through when
                // nothing is saved, because a reorder point always reads demand SOME way and
                // an unset toggle would misreport that as "no basis applies".
                setRopBasis(res.ropDrrBasis ?? 'BACKWARD');
                setRopBasisInherited(!res.isRopDrrBasisConfigured);
            })
            .catch(() => {
                if (!cancelled) setConfig(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [opened]);

    const patchDraft = (tier: string, field: keyof TierDraft, value: number | '') => {
        setDrafts((prev) =>
            prev.map((d) => (d.tier === tier ? { ...d, [field]: value } : d)),
        );
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            // Only tiers the brand actually filled in are sent. A blank stays absent so it
            // keeps inheriting the default rather than being frozen at today's value.
            const tierTargetDohDays: Record<string, number> = {};
            const reorderRules: Record<
                string,
                {
                    leadTimeDays: number | null;
                    safetyDays: number | null;
                    drrWindowDays: number | null;
                }
            > = {};
            for (const draft of drafts) {
                if (draft.targetDohDays !== '') {
                    tierTargetDohDays[draft.tier] = Number(draft.targetDohDays);
                }
                if (
                    draft.leadTimeDays !== '' ||
                    draft.safetyDays !== '' ||
                    draft.drrWindowDays !== ''
                ) {
                    reorderRules[draft.tier] = {
                        leadTimeDays:
                            draft.leadTimeDays === '' ? null : Number(draft.leadTimeDays),
                        safetyDays:
                            draft.safetyDays === '' ? null : Number(draft.safetyDays),
                        drrWindowDays:
                            draft.drrWindowDays === ''
                                ? null
                                : Number(draft.drrWindowDays),
                    };
                }
            }
            // Only the half being edited is sent. The endpoint patches, so omitting the
            // other half leaves it untouched — editing reorder rules can never silently
            // rewrite the plan's cover targets from a stale copy held in this component.
            const saved = await updateReplenishmentConfig(
                isTargetDoh
                    ? {
                          tierScope: scope,
                          defaultTargetDohDays:
                              defaultDoh === '' ? 30 : Number(defaultDoh),
                          tierTargetDohDays:
                              Object.keys(tierTargetDohDays).length > 0
                                  ? tierTargetDohDays
                                  : null,
                      }
                    : {
                          tierScope: scope,
                          defaultReorderSafetyDays:
                              defaultSafety === '' ? 0 : Number(defaultSafety),
                          // Null CLEARS the merchant default, handing every un-tiered SKU back
                          // to the org's sales lookback. That is what a blank box means here,
                          // and it has to travel as an explicit null rather than being omitted
                          // — the endpoint patches, so an absent field would leave a previously
                          // saved window in place and the operator's clear would do nothing.
                          defaultRopDrrWindowDays:
                              defaultRopWindow === '' ? null : Number(defaultRopWindow),
                          // Sent whether or not it was touched. Unlike the window there is no
                          // blank state to preserve: the toggle always shows a real direction,
                          // so saving pins whatever is on screen — which is what an operator
                          // who has just read it expects, inherited value or not.
                          ropDrrBasis: ropBasis,
                          reorderRules:
                              Object.keys(reorderRules).length > 0 ? reorderRules : null,
                      },
            );
            setConfig(saved);
            showToastSuccess({
                title: isTargetDoh ? 'Target cover saved' : 'Reorder settings saved',
                subtitle: isTargetDoh
                    ? 'The next plan run will size against these.'
                    : 'Inventory health will use these on its next read.',
            });
            onClose();
        } catch {
            showToastWarning({ title: 'Could not save the rules. Please try again.' });
        } finally {
            setSaving(false);
        }
    };

    const hasTiers = drafts.length > 0;
    const isBackwardRop = ropBasis === 'BACKWARD';
    /** What a blank default window actually resolves to, for the copy and the tier placeholders. */
    const effectiveRopWindow = defaultRopWindow || inheritedRopWindow || 30;

    return (
        <MacWindowModal
            opened={opened}
            onClose={onClose}
            title={isTargetDoh ? 'Target cover' : 'Reorder settings'}
            size={760}
        >
            {loading && (
                <Group justify="center" py="xl">
                    <Loader size="sm" />
                </Group>
            )}

            {!loading && (
                <Stack gap="lg">
                    <Stack gap={4}>
                        <Text size="13px" fw={600}>
                            Tier scope
                        </Text>
                        <Text size="xs" c="dimmed">
                            Your tier definition is applied to every scope when tiers are
                            recalculated, so this only chooses which ranking replenishment
                            READS. One setting, shared by target cover and the reorder point
                            — changing it here changes both. Distribution keeps making its
                            own, channel-level choice.
                        </Text>
                        <Select
                            size="xs"
                            w={280}
                            mt={4}
                            value={scope}
                            onChange={(value) =>
                                setScope((value as ReplenishmentTierScope) ?? 'BRAND')
                            }
                            data={SCOPE_OPTIONS}
                            allowDeselect={false}
                        />
                        <Text size="xs" c="dimmed" mt={4}>
                            {SCOPE_HINT[scope]}
                        </Text>
                    </Stack>

                    {!isTargetDoh && (
                        <>
                            <Divider />
                            <Stack gap={4}>
                                <Text size="13px" fw={600}>
                                    How the reorder point reads demand
                                </Text>
                                <Text size="xs" c="dimmed">
                                    The rate the threshold multiplies:{' '}
                                    <b>DRR x (lead time + safety)</b>. This is the brand&apos;s
                                    COMMITMENT and is stored here, deliberately apart from the
                                    DRR control on the grid — that one re-bases what is on
                                    screen so you can explore, and dragging it must not re-size
                                    a threshold you buy against.
                                </Text>
                                <Group gap="lg" wrap="nowrap" align="flex-end" mt={6}>
                                    <Stack gap={2}>
                                        <Text size="xs" c="dimmed">
                                            Basis
                                        </Text>
                                        <SegmentedControl
                                            size="xs"
                                            w={230}
                                            value={ropBasis}
                                            onChange={(value) =>
                                                setRopBasis(value as RatePolicyBasis)
                                            }
                                            data={[
                                                { label: 'Past Sales', value: 'BACKWARD' },
                                                { label: 'Forecast', value: 'FORWARD' },
                                            ]}
                                        />
                                    </Stack>
                                    {/*
                                      * Only past sales has a window to set. On forecast the
                                      * days are decided by the question itself — see the note
                                      * below — so a lookback control there would be a knob
                                      * wired to nothing, and a saved value on it would sit
                                      * there looking as if it applied.
                                      */}
                                    {isBackwardRop && (
                                        <Stack gap={2}>
                                            <Text size="xs" c="dimmed">
                                                Default window
                                            </Text>
                                            <NumberInput
                                                size="xs"
                                                w={140}
                                                min={1}
                                                max={365}
                                                // The placeholder names what is inherited when
                                                // this is blank, so an empty box reads as "the
                                                // org's lookback applies" rather than as "no
                                                // window applies". There is always a window.
                                                placeholder={`${inheritedRopWindow ?? 30} (org default)`}
                                                value={defaultRopWindow}
                                                onChange={(value) =>
                                                    setDefaultRopWindow(
                                                        value === '' ? '' : Number(value),
                                                    )
                                                }
                                            />
                                        </Stack>
                                    )}
                                </Group>
                                <Text size="xs" c="dimmed" mt={4}>
                                    {isBackwardRop
                                        ? `Run rate = sales over the last ${effectiveRopWindow} days ÷ ${effectiveRopWindow}. Reorder point = that rate × (lead time + safety).`
                                        : 'Reorder point = the forecast over the days the order has to cover — lead time + safety — pro-rated from however you uploaded it. A SKU on a 90d lead with 30d safety is sized on the next 120 days of forecast. No lookback is involved, so there is no window to set.'}
                                    {ropBasisInherited
                                        ? ' Currently inherited from your organisation default.'
                                        : ''}
                                </Text>
                                {!isBackwardRop && (
                                    <Text size="xs" c="orange.7">
                                        A SKU with no forecast is sized at zero and stops
                                        triggering. Upload forecasts before switching.
                                    </Text>
                                )}
                                {isBackwardRop && (
                                    <Text size="xs" c="dimmed">
                                        One window for every SKU. Override it per tier below, or
                                        per SKU in bulk
                                        {onOpenUpload ? (
                                            <>
                                                {' '}
                                                <Anchor
                                                    component="button"
                                                    type="button"
                                                    size="xs"
                                                    onClick={onOpenUpload}
                                                >
                                                    by uploading a CSV
                                                </Anchor>
                                            </>
                                        ) : (
                                            ' by CSV upload'
                                        )}
                                        .
                                    </Text>
                                )}
                                <Text size="xs" c="dimmed">
                                    The basis is not overridable per tier or per SKU — a brand
                                    buys one way, and two thresholds on one screen read in
                                    different directions could not be compared.
                                </Text>
                            </Stack>
                        </>
                    )}

                    <Divider />

                    <Stack gap={4}>
                        <Text size="13px" fw={600}>
                            {isTargetDoh ? 'Target cover per tier' : 'Reorder rule per tier'}
                        </Text>
                        <Text size="xs" c="dimmed">
                            {isTargetDoh
                                ? 'How much cover the plan holds for each tier. It works out WHEN to order from lead times on its own — you never state a date.'
                                : 'The level inventory health flags on, derived rather than typed: DRR × (lead time + safety). Lead time defaults to the worst case across the SKU’s vendors. Target cover is the plan’s concept and deliberately plays no part here.'}
                        </Text>
                    </Stack>

                    {!hasTiers && (
                        <Text size="sm" c="dimmed">
                            No tiers defined yet. Set them up in Settings → Product Tier
                            Config first — these rules key off those labels.
                        </Text>
                    )}

                    {hasTiers && (
                        <div
                            className={
                                isTargetDoh
                                    ? classes.targetGrid
                                    : isBackwardRop
                                      ? classes.grid
                                      : classes.forecastGrid
                            }
                        >
                            <div className={macWindowClasses.columnHeader}>Tier</div>
                            {isTargetDoh ? (
                                <div className={macWindowClasses.columnHeader}>
                                    Target cover (days)
                                </div>
                            ) : (
                                <>
                                    {/* Same reasoning as the default window above: on a
                                        forecast basis there is no lookback to tune. */}
                                    {isBackwardRop && (
                                        <div className={macWindowClasses.columnHeader}>
                                            DRR window (days)
                                        </div>
                                    )}
                                    <div className={macWindowClasses.columnHeader}>
                                        Lead time (days)
                                    </div>
                                    <div className={macWindowClasses.columnHeader}>
                                        Safety (days)
                                    </div>
                                </>
                            )}

                            {drafts.map((draft) => (
                                <Fragment key={draft.tier}>
                                    <Text size="13px" fw={500}>
                                        {draft.tier}
                                    </Text>
                                    {isTargetDoh ? (
                                        <NumberInput
                                            size="xs"
                                            min={1}
                                            max={365}
                                            placeholder={String(defaultDoh || 30)}
                                            value={draft.targetDohDays}
                                            onChange={(value) =>
                                                patchDraft(
                                                    draft.tier,
                                                    'targetDohDays',
                                                    value === '' ? '' : Number(value),
                                                )
                                            }
                                        />
                                    ) : (
                                        <>
                                            {/*
                                              * The term that decides the RATE the other two
                                              * multiply. A short window tracks a trend, a long
                                              * one refuses to chase noise — which is why it
                                              * belongs per tier: A-tier is usually worth
                                              * following closely and C-tier usually is not.
                                              *
                                              * Past sales only. Any value already saved here
                                              * is kept, not cleared — flipping the basis to
                                              * look at forecast and back must not silently
                                              * discard the brand's tier windows.
                                              */}
                                            {isBackwardRop && (
                                            <NumberInput
                                                size="xs"
                                                min={1}
                                                max={365}
                                                placeholder={String(effectiveRopWindow)}
                                                value={draft.drrWindowDays}
                                                onChange={(value) =>
                                                    patchDraft(
                                                        draft.tier,
                                                        'drrWindowDays',
                                                        value === '' ? '' : Number(value),
                                                    )
                                                }
                                            />
                                            )}
                                            <NumberInput
                                                size="xs"
                                                min={0}
                                                max={365}
                                                placeholder="from lanes"
                                                value={draft.leadTimeDays}
                                                onChange={(value) =>
                                                    patchDraft(
                                                        draft.tier,
                                                        'leadTimeDays',
                                                        value === '' ? '' : Number(value),
                                                    )
                                                }
                                            />
                                            <NumberInput
                                                size="xs"
                                                min={0}
                                                max={365}
                                                placeholder={String(defaultSafety || 0)}
                                                value={draft.safetyDays}
                                                onChange={(value) =>
                                                    patchDraft(
                                                        draft.tier,
                                                        'safetyDays',
                                                        value === '' ? '' : Number(value),
                                                    )
                                                }
                                            />
                                        </>
                                    )}
                                </Fragment>
                            ))}
                        </div>
                    )}

                    <Divider />

                    <Group gap="lg" wrap="nowrap" align="flex-end">
                        {isTargetDoh && (
                        <Stack gap={2}>
                            <Text size="xs" c="dimmed">
                                Default target cover
                            </Text>
                            <NumberInput
                                size="xs"
                                w={140}
                                min={1}
                                max={365}
                                value={defaultDoh}
                                onChange={(value) =>
                                    setDefaultDoh(value === '' ? '' : Number(value))
                                }
                            />
                        </Stack>
                        )}
                        {/* The default window moved up into "How the reorder point reads
                          * demand", where it sits beside the basis it is measured in. Split
                          * across two sections it read as two unrelated numbers. */}
                        {!isTargetDoh && (
                        <Stack gap={2}>
                            <Text size="xs" c="dimmed">
                                Default safety
                            </Text>
                            <NumberInput
                                size="xs"
                                w={140}
                                min={0}
                                max={365}
                                value={defaultSafety}
                                onChange={(value) =>
                                    setDefaultSafety(value === '' ? '' : Number(value))
                                }
                            />
                        </Stack>
                        )}
                    </Group>

                    {config && !config.isConfigured && isTargetDoh && (
                        <Text size="xs" c="dimmed">
                            Nothing saved yet — the values above are what the plan uses
                            today, taken from your under-stock limit.
                        </Text>
                    )}

                    <Group justify="flex-end" gap="xs">
                        <Button size="xs" variant="subtle" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button size="xs" loading={saving} onClick={handleSave}>
                            {isTargetDoh ? 'Save target cover' : 'Save reorder settings'}
                        </Button>
                    </Group>
                </Stack>
            )}
        </MacWindowModal>
    );
};

export default ReplenishmentSettingsModal;
